"use client";

import { ElementType, useRef } from "react";
import { PressEvent, useButton } from "react-aria";
import { useTheme } from "./ThemeProvider";

import { csx, isUrlExternal } from "../utils";

import "./Button.css";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "success"
  | "danger"
  | "translucent";

export const ButtonVariants: Record<ButtonVariant, ButtonVariant> = {
  primary: "primary",
  secondary: "secondary",
  success: "success",
  danger: "danger",
  translucent: "translucent",
};

export type ButtonSize =
  | "fullWidth"
  | "small"
  | "medium"
  | "large"
  | "xlarge"
  | "2xlarge";

export const ButtonSizes: Record<ButtonSize, ButtonSize> = {
  fullWidth: "fullWidth",
  small: "small",
  medium: "medium",
  large: "large",
  xlarge: "xlarge",
  "2xlarge": "2xlarge",
};

export interface ButtonProps {
  /** The button's visual aesthetic
   * @param {ButtonVariant} variant
   */
  variant?: ButtonVariant;

  /** The size of the button
   * @param {ButtonSize} size
   * @default "fullWidth"
   */
  size?: ButtonSize;

  /** The prefix to display within the button
   * @param {React.ReactNode} icon
   */
  icon?: React.ReactNode;

  /** Should the button be non-interactive?
   * @param {boolean} isDisabled
   */
  isDisabled?: boolean;

  /** The URL that the button should link to. Turns the element into an `a` tag. If the URL is external, you should pass the entire URL to the `to` prop (e.g. `https://example.com`).
   * @param {string} to
   */
  to?: string;

  /** The target attribute for the link. Defaults to `_blank` if the URL is external.
   * @param {string} target
   */
  target?: "_blank" | "_self" | "_parent" | "_top";

  /** Called when the button is pressed (on release, not keydown)
   * @param {(e: PressEvent) => void} onPress
   */
  onPress?: (e: PressEvent) => void;

  /** The type of button
   * @param {"button" | "submit" | "reset"} type
   */
  type?: "button" | "submit" | "reset";

  /** The test ID for the button
   * @param {string} dataTestId
   */
  "data-testid"?: string;

  /** The content to display within the button
   * @param {React.ReactNode} children
   */
  children?: React.ReactNode;
}

/**
 * A customizable button component that can render as either a button or anchor element
 * @interface ButtonProps
 */
function Button({
  variant = "primary",
  size = "fullWidth",
  icon,
  to,
  target,
  onPress,
  type,
  isDisabled,
  "data-testid": testId,
  children,
}: ButtonProps) {
  const Element: ElementType = to ? "a" : "button";

  const isExternal = to && isUrlExternal(to);

  const linkProps =
    Element !== "a"
      ? {}
      : {
          target: target || (isExternal ? "_blank" : undefined),
          href: to,
          rel:
            isExternal || target === "_blank"
              ? "noopener noreferrer"
              : undefined,
        };

  const { className: themeClassName } = useTheme();
  const ref = useRef(null);
  const { buttonProps } = useButton(
    {
      onPress,
      elementType: Element,
      type: type || "button",
      isDisabled,
    },
    ref
  );

  return (
    <Element
      {...buttonProps}
      {...linkProps}
      className={csx(
        "proton-Button",
        `proton-Button--${variant}`,
        isDisabled && "proton-Button--disabled",
        size && `proton-Button--${size}`,
        themeClassName
      )}
      ref={ref}
      data-testid={testId}
    >
      {icon}
      {children}
      {to && isExternal && (
        <svg viewBox="0 0 512 512" width="0.8em" height="0.8em">
          <path
            d="M384 224v184a40 40 0 01-40 40H104a40 40 0 01-40-40V168a40 40 0 0140-40h167.48M336 64h112v112M224 288L440 72"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="32"
          />
        </svg>
      )}
    </Element>
  );
}

export default Button;
